/**
 * @file On-screen twin-stick controls for touch devices.
 *
 * This is a *device* layer, not a gameplay one: it turns fingers into the same
 * abstract `Action`s and analog sticks a gamepad already produces, and stops
 * there. `PlayerControlSystem` and friends never learn that touch exists —
 * `InputManager.analogActive` is the only seam, and it was already open for the
 * gamepad (see {@link ../input/InputManager.js}).
 *
 * Layout: a floating stick per bottom corner (left moves, right aims) and a
 * button cluster. The sticks float rather than sit at a fixed spot because a
 * thumb cannot find a fixed target it is not looking at — wherever the finger
 * lands becomes the centre.
 */

import { Action } from '../input/InputManager.js';
import { Events } from '../core/EventBus.js';
import { GameStates } from '../core/states/StateMachine.js';
import { emptyWeaponAction } from '../combat/ammoPolicy.js';

/**
 * Buttons the cluster shows, in order. This table is the single source of truth
 * for the on-screen controls *and* the Inputs legend (`HUD.inputRows`), which is
 * what stops the two from drifting apart — the touch counterpart of the
 * `keyLabelsFor()` rule the keyboard legend follows.
 *
 * `contextual` buttons stay hidden until the game says they are usable.
 *
 * @type {ReadonlyArray<{ id: string, action: string, label: string, contextual?: boolean }>}
 */
export const TOUCH_BUTTONS = Object.freeze([
  { id: 'dash', action: Action.DASH, label: 'Dash' },
  { id: 'reload', action: Action.RELOAD, label: 'Reload' },
  { id: 'throw', action: Action.THROW, label: 'Throw' },
  { id: 'swap', action: Action.INTERACT, label: 'Swap', contextual: true }
]);

/** Finger travel, in CSS pixels, that reads as full stick deflection. */
const STICK_RADIUS = 56;

/**
 * Deflection below which a stick reads as centred. Keeps a resting thumb from
 * trickling movement, and — because the aim stick also pulls the trigger — from
 * firing the moment it is touched.
 */
const DEAD_ZONE = 0.18;

export class TouchControls {
  /**
   * @param {HTMLElement} root Host element (the shared `#ui-root`).
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   */
  constructor(root, ctx) {
    /** @type {HTMLElement} @private */
    this._host = root;
    /** @type {import('../core/Context.js').GameContext} @private */
    this._ctx = ctx;
    /** @type {HTMLElement | null} @private */
    this._el = null;
    /** @type {Record<string, HTMLElement>} @private */
    this._nodes = {};
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {Array<[EventTarget, string, EventListener, any]>} @private */
    this._listeners = [];
    /**
     * Which pointer owns which stick. Tracked by id so two thumbs work at once
     * and the second one down cannot steal the first one's stick.
     * @type {Map<number, { which: 'move' | 'aim', originX: number, originY: number }>}
     * @private
     */
    this._sticks = new Map();
    /**
     * Last live aim direction, kept so lifting the thumb holds the facing
     * instead of dropping it. Seeded pointing "up" the screen so the very first
     * frame of a run has a direction that is not the raycast fallback.
     * @type {{ x: number, y: number }}
     * @private
     */
    this._lastAim = { x: 0, y: -1 };
    /** @type {boolean} Last `INTERACTION_PROMPT` visibility, to skip no-op writes. @private */
    this._promptVisible = false;
    /** @type {string} Latest game state. @private */
    this._state = '';

    this._build();
    this._subscribe();
    this.setVisible(false);
  }

  /** @returns {HTMLElement | null} Root element, for tests. */
  get element() {
    return this._el;
  }

  /** @returns {void} @private */
  _build() {
    const el = document.createElement('div');
    el.className = 'touch-controls';
    el.innerHTML = `
      <button type="button" class="touch-pause" data-touch="pause" aria-label="Pause">&#10074;&#10074;</button>
      <div class="touch-zone touch-zone--move" data-touch-stick="move">
        <div class="touch-stick" data-touch="moveStick"><span class="touch-stick__knob"></span></div>
      </div>
      <div class="touch-zone touch-zone--aim" data-touch-stick="aim">
        <div class="touch-stick" data-touch="aimStick"><span class="touch-stick__knob"></span></div>
      </div>
      <div class="touch-buttons">
        ${TOUCH_BUTTONS.map(
          (button) => `
            <button type="button"
                    class="touch-button touch-button--${button.id}${button.contextual ? ' touch-button--contextual' : ''}"
                    data-touch-action="${button.action}"
                    data-touch-id="${button.id}">${button.label}</button>
          `
        ).join('')}
      </div>
      <div class="touch-tap" data-touch="tap"></div>
    `;

    for (const node of el.querySelectorAll('[data-touch]')) {
      this._nodes[/** @type {HTMLElement} */ (node).dataset.touch] = /** @type {HTMLElement} */ (node);
    }
    this._host.appendChild(el);
    this._el = el;

    this._bindSticks();
    this._bindButtons();
  }

  /** @returns {void} @private */
  _bindSticks() {
    const add = (target, type, handler) => {
      target.addEventListener(type, handler);
      this._listeners.push([target, type, handler, undefined]);
    };

    for (const zone of this._el.querySelectorAll('[data-touch-stick]')) {
      const which = /** @type {'move' | 'aim'} */ (
        /** @type {HTMLElement} */ (zone).dataset.touchStick
      );

      add(zone, 'pointerdown', (event) => {
        const pointer = /** @type {PointerEvent} */ (event);
        event.preventDefault();

        // Claim the stick *before* attempting capture. `setPointerCapture`
        // throws NotFoundError whenever the id is not an active pointer — a
        // contact that ended between the event and this handler, or a synthetic
        // event — and an exception here used to abandon the whole gesture.
        this._sticks.set(pointer.pointerId, {
          which,
          originX: pointer.clientX,
          originY: pointer.clientY
        });
        this._placeStick(which, pointer.clientX, pointer.clientY);
        this._writeStick(which, 0, 0);

        // Capture is an optimisation, not a requirement: it keeps a thumb that
        // slides out of its zone driving the stick. The window-level listeners
        // below already cover the uncaptured case.
        try {
          /** @type {any} */ (zone).setPointerCapture?.(pointer.pointerId);
        } catch {
          // Pointer already gone; the gesture above still stands.
        }
      });
    }

    // Move/up live on `window`: a finger can leave the zone, and a pointercancel
    // (a system gesture stealing the touch) must release the stick just as an
    // ordinary lift does, or the player runs into a wall forever.
    add(window, 'pointermove', (event) => {
      const pointer = /** @type {PointerEvent} */ (event);
      const stick = this._sticks.get(pointer.pointerId);
      if (!stick) return;
      event.preventDefault();

      const dx = (pointer.clientX - stick.originX) / STICK_RADIUS;
      const dy = (pointer.clientY - stick.originY) / STICK_RADIUS;
      const length = Math.hypot(dx, dy);
      const scale = length > 1 ? 1 / length : 1;
      this._writeStick(stick.which, dx * scale, dy * scale);
    });

    const release = (event) => {
      const pointer = /** @type {PointerEvent} */ (event);
      const stick = this._sticks.get(pointer.pointerId);
      if (!stick) return;
      this._sticks.delete(pointer.pointerId);
      this._endStick(stick.which);
      this._resetStickVisual(stick.which);
    };
    add(window, 'pointerup', release);
    add(window, 'pointercancel', release);
  }

  /** @returns {void} @private */
  _bindButtons() {
    const add = (target, type, handler) => {
      target.addEventListener(type, handler);
      this._listeners.push([target, type, handler, undefined]);
    };
    const { input } = this._ctx;

    for (const node of this._el.querySelectorAll('[data-touch-action]')) {
      const action = /** @type {HTMLElement} */ (node).dataset.touchAction;
      add(node, 'pointerdown', (event) => {
        event.preventDefault();
        input.pressVirtual(action);
      });
      // Release on `pointerup` *and* `pointerleave`: a finger that slides off a
      // button would otherwise leave the action held down for good.
      add(node, 'pointerup', () => input.releaseVirtual(action));
      add(node, 'pointerleave', () => input.releaseVirtual(action));
    }

    add(this._nodes.pause, 'pointerdown', (event) => {
      event.preventDefault();
      input.pressVirtual(Action.PAUSE);
    });
    add(this._nodes.pause, 'pointerup', () => input.releaseVirtual(Action.PAUSE));

    // GAME_OVER's overlay is `layout: 'simple'` and renders no nav buttons, so
    // a tap on the canvas (read as FIRE) was the only way off it — and touch
    // taps no longer reach that binding. Without this surface the death screen
    // is a dead end on a phone. CONFIRM, not FIRE: it is the same edge Enter
    // raises, which `GameOverState` already answers.
    add(this._nodes.tap, 'pointerdown', (event) => {
      event.preventDefault();
      input.pressVirtual(Action.CONFIRM);
    });
    add(this._nodes.tap, 'pointerup', () => input.releaseVirtual(Action.CONFIRM));
  }

  /** @returns {void} @private */
  _subscribe() {
    const { events } = this._ctx;
    this._subscriptions.push(
      events.on(Events.STATE_CHANGED, ({ state }) => this.setState(state)),
      events.on(Events.INTERACTION_PROMPT, ({ visible }) => {
        // Re-emitted every frame a target is in range with a reused payload:
        // read the flag, never keep it, and only touch the DOM on a change.
        if (visible === this._promptVisible) return;
        this._promptVisible = visible;
        this._setReady('swap', visible);
      }),
      // Same events and the same decision function HUD's centre-screen prompt
      // uses (`emptyWeaponAction`) — reading the identical weapon state is what
      // keeps the pad's glow from ever disagreeing with that text.
      events.on(Events.WEAPON_EMPTY, ({ weapon }) => this._setEmptyAction(emptyWeaponAction(weapon), weapon)),
      events.on(Events.WEAPON_EMPTY_TRIGGER, ({ weapon }) =>
        this._setEmptyAction(emptyWeaponAction(weapon), weapon)
      ),
      events.on(Events.RELOAD_STARTED, () => this._setEmptyAction(null)),
      events.on(Events.RELOAD_COMPLETED, () => this._setEmptyAction(null)),
      events.on(Events.RELOAD_INTERRUPTED, () => this._setEmptyAction(null)),
      // A fresh weapon starts its own ammo story (mirrors HUD._hideEmptyPrompt
      // on the same event).
      events.on(Events.WEAPON_CHANGED, () => this._setEmptyAction(null))
    );
  }

  /**
   * Writes a stick through to the input layer, applying the dead zone.
   *
   * The aim stick doubles as the trigger: past the dead zone it holds `FIRE`,
   * which is what makes this a twin-stick shooter rather than a game with a
   * fire button nobody has a third thumb for.
   *
   * @param {'move' | 'aim'} which Which stick.
   * @param {number} x -1..1.
   * @param {number} y -1..1.
   * @returns {void}
   * @private
   */
  _writeStick(which, x, y) {
    const length = Math.hypot(x, y);
    const live = length >= DEAD_ZONE;
    const outX = live ? x : 0;
    const outY = live ? y : 0;

    this._ctx.input.setTouchStick(which, outX, outY);
    this._moveKnob(which, outX, outY);

    if (which === 'aim') {
      if (live) {
        this._lastAim.x = outX;
        this._lastAim.y = outY;
        this._ctx.input.pressVirtual(Action.FIRE);
      } else {
        this._ctx.input.releaseVirtual(Action.FIRE);
      }
    }
  }

  /**
   * Ends a stick's contact.
   *
   * Movement stops dead, but aim is *retained*: a zeroed aim stick sends
   * `PlayerControlSystem._applyAim` down its cursor-raycast branch, and the
   * touch pointer is deliberately never updated, so the body would snap to face
   * whatever screen centre projects onto the moment a thumb lifted.
   *
   * @param {'move' | 'aim'} which Which stick.
   * @returns {void}
   * @private
   */
  _endStick(which) {
    if (which === 'aim') {
      this._ctx.input.releaseVirtual(Action.FIRE);
      this._ctx.input.setTouchStick('aim', this._lastAim.x, this._lastAim.y);
      this._moveKnob('aim', 0, 0);
      return;
    }
    this._writeStick('move', 0, 0);
  }

  /**
   * @param {'move' | 'aim'} which Which stick.
   * @param {number} clientX Viewport X the finger landed on.
   * @param {number} clientY Viewport Y the finger landed on.
   * @returns {void}
   * @private
   */
  _placeStick(which, clientX, clientY) {
    const stick = this._nodes[`${which}Stick`];
    if (!stick) return;
    stick.style.left = `${clientX}px`;
    stick.style.top = `${clientY}px`;
    stick.classList.add('touch-stick--active');
  }

  /**
   * @param {'move' | 'aim'} which Which stick.
   * @param {number} x -1..1.
   * @param {number} y -1..1.
   * @returns {void}
   * @private
   */
  _moveKnob(which, x, y) {
    const knob = this._nodes[`${which}Stick`]?.querySelector('.touch-stick__knob');
    if (!knob) return;
    /** @type {HTMLElement} */ (knob).style.transform =
      `translate(${x * STICK_RADIUS}px, ${y * STICK_RADIUS}px)`;
  }

  /**
   * @param {'move' | 'aim'} which Which stick.
   * @returns {void}
   * @private
   */
  _resetStickVisual(which) {
    this._nodes[`${which}Stick`]?.classList.remove('touch-stick--active');
    this._moveKnob(which, 0, 0);
  }

  /**
   * Marks a button as usable right now: a breathing glow, the touch pad's
   * equivalent of the desktop centre-screen prompt. `.touch-button--contextual`
   * buttons (Swap) also gain visibility from this; Reload/Throw are always on
   * screen and only pick up the glow.
   *
   * @param {string} id Button id from {@link TOUCH_BUTTONS}.
   * @param {boolean} ready Whether the action can be used right now.
   * @returns {void}
   * @private
   */
  _setReady(id, ready) {
    const node = this._el?.querySelector(`[data-touch-id="${id}"]`);
    node?.classList.toggle('touch-button--ready', Boolean(ready));
  }

  /**
   * @param {'reload' | 'throw' | null} action Which button should glow, if any.
   * @param {import('../components/Weapon.js').Weapon | null} [weapon] The empty
   *   weapon, so a `'throw'` action can be checked against `weapon.throwable`.
   *   Unlike the desktop prompt (which still shows the text with its key badge
   *   hidden), a button that does nothing must never glow as if it did.
   * @returns {void}
   * @private
   */
  _setEmptyAction(action, weapon = null) {
    this._setReady('reload', action === 'reload');
    this._setReady('throw', action === 'throw' && weapon?.throwable !== false);
  }

  /**
   * Shows the pad while playing and the tap surface on the death screen.
   *
   * Visibility is decided here rather than by a `.hud--overlay-open` CSS rule,
   * because the two surfaces need opposite polarity — one hides behind an
   * overlay, the other only exists behind one — and two sources of truth for
   * the same thing drift.
   *
   * @param {string} state Current {@link GameStates} value.
   * @returns {void}
   */
  setState(state) {
    this._state = state;
    if (!this._el) return;

    const playing = state === GameStates.PLAYING;
    this._el.classList.toggle('touch-controls--visible', playing);
    this._nodes.tap?.classList.toggle('touch-tap--visible', state === GameStates.GAME_OVER);

    // Leaving PLAYING has to zero the sticks: `releaseAll()` drops held actions
    // but knows nothing about analog state, so the player would keep drifting
    // and firing the moment the pause menu closed.
    if (!playing) this._releaseEverything();
  }

  /**
   * @param {boolean} visible Whether the pad should be on screen.
   * @returns {void}
   */
  setVisible(visible) {
    this.setState(visible ? GameStates.PLAYING : GameStates.MENU);
  }

  /** @returns {void} @private */
  _releaseEverything() {
    const { input } = this._ctx;
    this._sticks.clear();
    input.clearTouchSticks();
    input.releaseVirtual(Action.FIRE);
    for (const button of TOUCH_BUTTONS) input.releaseVirtual(button.action);
    // Movement stops, facing does not: same reason as `_endStick`, so a pause
    // does not spin the player to face screen centre on resume.
    input.setTouchStick('aim', this._lastAim.x, this._lastAim.y);
    this._resetStickVisual('move');
    this._resetStickVisual('aim');
    // Stale glow would otherwise survive a pause and light a button whose
    // weapon/target is no longer what earned it the highlight.
    this._promptVisible = false;
    this._setReady('swap', false);
    this._setEmptyAction(null);
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    for (const [target, type, handler, options] of this._listeners) {
      target.removeEventListener(type, handler, options);
    }
    this._listeners.length = 0;
    this._el?.remove();
    this._el = null;
    this._nodes = {};
  }
}
